import { Link } from "react-router-dom";
import { Activity, Tv, Bike, BookOpen, Share2, BarChart3, LogOut, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import ShareDemoModal from "@/components/ShareDemoModal";

const VIEWS = [
  {
    key: "racing",
    path: "/racing",
    icon: Activity,
    colour: "#F97316",
    title: "Racing Intelligence",
    subtitle: "Live Field Analysis",
    description: "Real-time scoring of every runner's true performance — efficiency, pressure, momentum, and ground lost — distilled into one-sentence verdicts and next-start signals.",
    features: ["True rank vs visible position", "Efficiency & pressure bands", "Next-start upgrade/downgrade signals"],
  },
  {
    key: "broadcast",
    path: "/broadcast",
    icon: Tv,
    colour: "#EF4444",
    title: "Broadcast Overlay",
    subtitle: "Television Graphics Layer",
    description: "Compositable chroma-key graphics with real-time callouts, runner tags, and AI-generated commentary — built for live broadcast integration.",
    features: ["Chroma-key compositing", "AI race commentary", "Producer hotkey controls"],
  },
  {
    key: "cycling",
    path: "/cross-sport",
    icon: Bike,
    colour: "#A855F7",
    title: "Cycling Vision",
    subtitle: "Cross-Sport Proof",
    description: "Same analytical engine, different discipline. Peloton pressure, breakaway strength, and chase momentum — proving the universal spine works beyond racing.",
    features: ["Breakaway vs peloton analysis", "Effort & pressure tracking", "Gap intelligence"],
  },
  {
    key: "library",
    path: "/library",
    icon: BookOpen,
    colour: "#F59E0B",
    title: "Race Library",
    subtitle: "The Intelligence Vault",
    description: "Every completed race stored as a searchable intelligence case. Why the race was won, replay timelines, and individual runner stories — intelligence that compounds.",
    features: ["Why This Race Was Won", "Replay event timelines", "Per-runner phase narratives"],
  },
];

const PresenterHubPage = ({ user, token, onLogout }) => {
  const [showShareModal, setShowShareModal] = useState(false);

  return (
    <div className="min-h-screen bg-[#090a0d] text-white flex flex-col" data-testid="presenter-hub-page">
      {/* Minimal header */}
      <header className="bg-[#0d0f12]/95 backdrop-blur-sm border-b border-white/10 px-6 h-14 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#F97316]" />
          <span className="font-heading text-sm font-bold tracking-wider text-white uppercase">Race Intelligence Live</span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowShareModal(true)} size="sm" className="bg-[#F97316] hover:bg-[#EA580C] text-white h-8" data-testid="hub-share-btn">
            <Share2 className="w-3 h-3 mr-1.5" /> Share Demo
          </Button>
          <Link to="/analytics">
            <Button variant="ghost" size="sm" className="text-white/40 hover:text-white h-8 px-2">
              <BarChart3 className="w-3.5 h-3.5" />
            </Button>
          </Link>
          <Button onClick={onLogout} variant="ghost" size="sm" className="text-white/30 hover:text-white h-8 px-2" data-testid="hub-logout-btn">
            <LogOut className="w-3.5 h-3.5" />
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-16 max-w-2xl">
          <div className="text-[#F97316] text-xs font-bold uppercase tracking-[0.3em] mb-3">UK Pilot v2</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white leading-tight mb-4" data-testid="hub-title">
            One engine, any sport.
          </h1>
          <p className="text-white/40 text-base leading-relaxed">
            Four views into the same intelligence spine. Select a module below to begin a guided demonstration.
          </p>
        </div>

        {/* View tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl w-full">
          {VIEWS.map((view) => (
            <ViewTile key={view.key} view={view} />
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-16 text-center">
          <p className="text-white/15 text-xs">
            Confidential. All access logged and watermarked. Logged in as <span className="text-white/30">{user?.username}</span>.
          </p>
        </div>
      </main>

      {showShareModal && <ShareDemoModal token={token} onClose={() => setShowShareModal(false)} />}
    </div>
  );
};

const ViewTile = ({ view }) => {
  const Icon = view.icon;
  return (
    <Link to={view.path} className="group" data-testid={`hub-tile-${view.key}`}>
      <div className="h-full rounded-2xl border border-white/10 bg-[#0d0f12]/80 p-6 transition-all duration-300 hover:border-white/20 hover:bg-[#0d0f12] cursor-pointer relative overflow-hidden">
        {/* Glow accent */}
        <div
          className="absolute top-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ background: `linear-gradient(90deg, transparent, ${view.colour}, transparent)` }}
        />

        {/* Icon + title */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${view.colour}15` }}>
            <Icon className="w-5 h-5" style={{ color: view.colour }} />
          </div>
          <div>
            <h2 className="font-heading text-base font-bold text-white group-hover:text-white transition-colors">{view.title}</h2>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: view.colour }}>{view.subtitle}</div>
          </div>
        </div>

        {/* Description */}
        <p className="text-white/40 text-sm leading-relaxed mb-5">{view.description}</p>

        {/* Feature list */}
        <div className="space-y-1.5 mb-6">
          {view.features.map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-white/30">
              <div className="w-1 h-1 rounded-full" style={{ backgroundColor: view.colour }} />
              {f}
            </div>
          ))}
        </div>

        {/* Launch button */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: view.colour }}>
            Launch
          </span>
          <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" style={{ color: view.colour }} />
        </div>
      </div>
    </Link>
  );
};

export default PresenterHubPage;
